import { Controller } from "@hotwired/stimulus"

// Typeform-style one-question-at-a-time wizard for the guest request page.
// All fields live in a single form; only one step is visible at a time and
// steps slide in from the direction of travel. Back/Next move between steps
// and only the last step can submit the form.
export default class extends Controller {
  static targets = ["step", "progress", "error"]

  connect() {
    this.index = 0
    this.buildProgress()
    this.show()
  }

  next(event) {
    if (event) event.preventDefault()
    if (!this.validateStep(this.currentStep)) return
    if (this.index < this.stepTargets.length - 1) {
      this.index += 1
      this.show("forward")
    }
  }

  prev(event) {
    if (event) event.preventDefault()
    if (this.index > 0) {
      this.index -= 1
      this.show("backward")
    }
  }

  // Also blocks the browser's implicit submission (Enter in a text field), which
  // would otherwise submit half-answered forms from an earlier step.
  submitForm(event) {
    const onLastStep = this.index === this.stepTargets.length - 1
    if (!onLastStep || !this.validateStep(this.currentStep)) {
      event.preventDefault()
    }
  }

  get currentStep() {
    return this.stepTargets[this.index]
  }

  show(direction = null) {
    this.stepTargets.forEach((step, i) => {
      step.hidden = i !== this.index
      step.classList.remove("ir-wizard__step--forward", "ir-wizard__step--backward")
    })

    if (direction) {
      const step = this.currentStep
      void step.offsetWidth // restart the CSS animation
      step.classList.add(`ir-wizard__step--${direction}`)
    }

    this.updateProgress()
    this.clearErrors()
    this.focusStep()
  }

  focusStep() {
    requestAnimationFrame(() => {
      const step = this.currentStep
      if (!step) return

      // Prefer the answer field; skip generator chrome (number/range/radio).
      const field = step.querySelector(
        ".ir-wizard__field input:not([type=hidden]):not([type=radio]):not([type=checkbox]):not([type=number]):not([type=range]), .ir-wizard__field textarea",
      )
      if (field) {
        field.focus({ preventScroll: true })
        return
      }

      const button = step.querySelector(".ir-wizard__actions .button--primary")
      if (button) button.focus({ preventScroll: true })
    })
  }

  // The welcome step isn't a question, so it gets no dot.
  get questionSteps() {
    return this.stepTargets.filter((step) => step.dataset.field)
  }

  buildProgress() {
    if (!this.hasProgressTarget) return

    const total = this.questionSteps.length
    this.progressTarget.replaceChildren()
    this.progressTarget.hidden = total < 2
    if (this.progressTarget.hidden) return

    this.progressTarget.setAttribute("aria-valuemin", "0")
    this.progressTarget.setAttribute("aria-valuemax", String(total))

    for (let i = 0; i < total; i += 1) {
      const dot = document.createElement("span")
      dot.className = "ir-wizard__progress-dot"
      dot.setAttribute("aria-hidden", "true")
      this.progressTarget.appendChild(dot)
    }
  }

  updateProgress() {
    if (!this.hasProgressTarget || this.progressTarget.hidden) return

    const position = this.questionSteps.indexOf(this.currentStep)
    Array.from(this.progressTarget.children).forEach((dot, i) => {
      dot.classList.toggle("ir-wizard__progress-dot--complete", position > i)
      dot.classList.toggle("ir-wizard__progress-dot--current", position === i)
    })

    this.progressTarget.setAttribute("aria-valuenow", String(position + 1))
  }

  validateStep(step) {
    if (!step || step.dataset.required !== "true") return true

    const name = step.dataset.field
    const input = step.querySelector(`[name="submission[${name}]"]`)
    const value = input && input.value ? input.value.trim() : ""

    if (value === "") {
      this.showError(step, "Please fill this in")
      if (input) input.focus()
      return false
    }
    return true
  }

  showError(step, message) {
    const el = step.querySelector("[data-info-request-wizard-target='error']")
    if (!el) return

    const label = el.querySelector("span")
    if (label) label.textContent = message
    el.hidden = false
  }

  clearErrors() {
    this.errorTargets.forEach((el) => {
      el.hidden = true
    })
  }
}
